'use client';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { ListPage } from '@/components/app/list-page';
import type { ListColumn } from '@/components/app/list-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useResolvedRoutes } from '@/lib/use-routes';
import { useNavigate } from '@tanstack/react-router';
import {
  CheckCircle2Icon,
  ClockIcon,
  InboxIcon,
  MoreVertical,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  type CampaignChannel,
  type CampaignListItem,
  type CampaignStatus,
  campaignChannelLabels,
  campaignStatusLabels,
  useDeleteCampaign,
  useListCampaigns,
  useSmsNumber,
} from '../api';
import { isChannelEnabled } from '../channels';

const statusVariant: Record<
  CampaignStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'outline',
  scheduled: 'secondary',
  sending: 'secondary',
  paused: 'secondary',
  sent: 'default',
  failed: 'destructive',
  cancelled: 'destructive',
};

function totalRecipients(campaign: CampaignListItem): number {
  const counts = campaign.recipientCounts ?? {};
  return Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0);
}

function audienceLine(campaign: CampaignListItem): string {
  const audience = campaign.segmentName ?? 'All leads';
  const total = totalRecipients(campaign);
  if (total === 0) return audience;
  return `${audience} · ${total} recipient${total === 1 ? '' : 's'}`;
}

/**
 * SMS sender status.
 *
 * An org's dedicated two-way number is now the ONLY way to send SMS — there is
 * no alphanumeric fallback — so "no active number" means SMS cannot send at
 * all. That has to be visible here rather than surfacing for the first time as
 * a launch blocker. Still no "buy a number" prompt: provisioning is the
 * specialist's job, so this states the situation without asking the customer
 * to act on it.
 */
function SmsSetupChip() {
  const { smsNumber, isLoading } = useSmsNumber();
  // Bulk messaging is email-only for now, so don't show SMS sender status for
  // a channel the org cannot currently send on.
  if (!isChannelEnabled('sms')) return null;
  if (isLoading) return null;

  if (!smsNumber || smsNumber.status !== 'active') {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
        <ClockIcon className="size-3.5" />
        SMS number being set up — your specialist will confirm when it's ready
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
      <CheckCircle2Icon className="size-3.5 text-primary" />
      SMS sends from {smsNumber.phoneNumber}
    </span>
  );
}

/**
 * Bulk Messaging index, on the shared `ListPage`.
 *
 * Replaces the desktop card grid AND the hand-written `CampaignsMobileList`
 * that sat beside it: the phone rows now come from the SAME column config the
 * table renders, so a column added here reaches both.
 */
export function CampaignList() {
  const navigate = useNavigate();
  const routes = useResolvedRoutes();
  const { campaigns, isLoading, isError, error, refetch } = useListCampaigns();
  const { deleteCampaign, isDeleting } = useDeleteCampaign();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CampaignListItem | null>(
    null
  );

  // Filtering stays HERE, not in the shell — what counts as a match differs
  // per list.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return campaigns;
    return campaigns.filter(
      (campaign) =>
        campaign.name.toLowerCase().includes(term) ||
        (campaign.segmentName ?? '').toLowerCase().includes(term)
    );
  }, [campaigns, search]);

  const columns: ListColumn<CampaignListItem>[] = [
    {
      id: 'name',
      header: 'Message',
      mobile: 'primary',
      cell: (campaign) => <span className="font-medium">{campaign.name}</span>,
    },
    {
      id: 'audience',
      header: 'Audience',
      mobile: 'secondary',
      cell: (campaign) => audienceLine(campaign),
    },
    {
      id: 'channels',
      header: 'Channels',
      cell: (campaign) => {
        const counts = campaign.recipientCounts ?? {};
        return (
          <div className="flex flex-wrap gap-1">
            {(campaign.channels as CampaignChannel[]).map((channel) => {
              const count = counts[channel] ?? 0;
              return (
                <Badge key={channel} variant="outline">
                  {campaignChannelLabels[channel] ?? channel}
                  {count > 0 && ` · ${count}`}
                </Badge>
              );
            })}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      align: 'right',
      // The status badge is what the phone row has always carried on its right
      // edge — the one value that decides whether this message still needs you.
      mobile: 'trailing',
      cell: (campaign) => (
        <Badge variant={statusVariant[campaign.status]}>
          {campaignStatusLabels[campaign.status]}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <title>Bulk Messaging | Borrah</title>

      <ListPage<CampaignListItem>
        config={{
          title: 'Bulk Messaging',
          description: 'Reach your leads by email.',
          columns,
          rows,
          rowKey: (campaign) => campaign.id,
          // Preserved from the deleted `CampaignsMobileList`, which emitted
          // this id on every phone row.
          rowTestId: (campaign) => `campaign-row-${campaign.id}`,
          onRowClick: (campaign) =>
            void navigate({
              to: routes.campaignDetail(campaign.id),
            }),
          rowActions: (campaign) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button aria-label="Row actions" size="icon" variant="ghost">
                  <MoreVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={isDeleting}
                  onClick={() => setDeleteTarget(campaign)}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
          searchPlaceholder: 'Search bulk messages',
          search,
          onSearchChange: setSearch,
          toolbar: <SmsSetupChip />,
          primaryAction: {
            label: 'New bulk message',
            mobileLabel: 'New',
            onClick: () => void navigate({ to: routes.campaignsNew }),
          },
          isLoading,
          isError,
          errorMessage: error?.message ?? 'Failed to load bulk messages',
          onRetry: () => void refetch(),
          empty: {
            icon: InboxIcon,
            title: search ? 'No matching messages' : 'No bulk messages yet',
            description: search
              ? 'No bulk message matches that search.'
              : 'Send your first message to reach your leads.',
          },
        }}
      />

      <ConfirmDeleteDialog
        description="This permanently deletes the bulk message and its delivery history. This can't be undone."
        isPending={isDeleting}
        onConfirm={() => {
          if (deleteTarget) deleteCampaign(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        open={deleteTarget !== null}
        title={<>Delete &ldquo;{deleteTarget?.name}&rdquo;?</>}
      />
    </>
  );
}
